import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Trash2,
  Check,
  X,
  ChevronDown,
  Users,
  ClipboardList,
  Calendar,
  ListTodo,
  Link2,
  Copy,
  RotateCcw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useUpdateProject,
  useUpdateMilestone,
  useCreateMilestone,
  useDeleteMilestone,
  useAddTeamMember,
  useRemoveTeamMember,
  useCreateTask,
  useUpdateTask,
  useDeleteTask,
  useShareLink,
  useCreateShareLink,
  useRevokeShareLink,
  useExtendShareLink,
  useUpdateShareSettings,
} from "@/hooks/useProjects";
import type { ProjectFull, ProjectMilestone, ProjectTask, ShareSettings } from "@/hooks/useProjects";
import { toast } from "sonner";

interface ManagementViewProps {
  project: ProjectFull;
  companies?: { id: string; name: string }[];
}

const statusLabel: Record<string, string> = { todo: "To Do", in_progress: "In Progress", done: "Done" };
const statusColor: Record<string, string> = {
  todo: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-900",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900",
  done: "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900",
};

export function ManagementView({ project, companies = [] }: ManagementViewProps) {
  const [showRecalculate, setShowRecalculate] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [newTaskName, setNewTaskName] = useState<Record<string, string>>({});
  const [teamOpen, setTeamOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // ── Pending changes (local state until Save) ──
  const [pendingProjectName, setPendingProjectName] = useState<string | null>(null);
  const [pendingStartDate, setPendingStartDate] = useState<string | null>(null);
  const [pendingCompanyId, setPendingCompanyId] = useState<string | null>(null);
  // milestoneId → { field: value } for changed fields
  const [pendingMilestoneChanges, setPendingMilestoneChanges] = useState<Record<string, Record<string, any>>>({});
  const [deleteConfirmMilestoneId, setDeleteConfirmMilestoneId] = useState<string | null>(null);
  const [addMilestoneOpen, setAddMilestoneOpen] = useState(false);
  const emptyNewMilestone = { name: "", priority: "Medium", owner: "__none__", startDate: "", dueDate: "", duration: 1 };
  const [newMilestone, setNewMilestone] = useState(emptyNewMilestone);
  const [cascadePrompt, setCascadePrompt] = useState<{
    editedName: string;
    deltaDays: number;
    downstream: { id: string; name: string; oldStartDate: string | null; oldDueDate: string | null }[];
  } | null>(null);

  const { data: crmUsers = [] } = useQuery<{ id: string; name: string; email: string }[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const res = await fetch("/api/users", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch users");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const updateProject = useUpdateProject(project.id);
  const updateMilestone = useUpdateMilestone(project.id);
  const createMilestone = useCreateMilestone(project.id);
  const deleteMilestone = useDeleteMilestone(project.id);
  const addTeamMember = useAddTeamMember(project.id);
  const removeTeamMember = useRemoveTeamMember(project.id);
  const createTask = useCreateTask(project.id);
  const updateTask = useUpdateTask(project.id);
  const deleteTask = useDeleteTask(project.id);

  const { milestones, teamMembers, activityLogs } = project;

  const availableUsers = crmUsers.filter(
    (u) => !project.teamMemberRecords.some((tm) => tm.userId === u.id)
  );

  // Get the current display value for a milestone field (pending or server)
  const getMilestoneVal = (m: ProjectMilestone, field: string) => {
    const pending = pendingMilestoneChanges[m.id];
    if (pending && field in pending) return pending[field];
    return (m as any)[field];
  };

  // Queue a milestone change locally
  const queueMilestoneChange = (milestoneId: string, field: string, value: any) => {
    setPendingMilestoneChanges((prev) => ({
      ...prev,
      [milestoneId]: { ...(prev[milestoneId] || {}), [field]: value },
    }));
  };

  // Check if there are unsaved changes
  const hasChanges = pendingProjectName !== null || pendingStartDate !== null || pendingCompanyId !== null || Object.keys(pendingMilestoneChanges).length > 0;

  // Save all pending changes
  // Convert a "YYYY-MM-DD" or ISO string to YYYY-MM-DD for shifted output.
  const toYMD = (val: string | Date): string => {
    if (val instanceof Date) return val.toISOString().split("T")[0];
    if (typeof val === "string" && val.length === 10) return val;
    return new Date(val).toISOString().split("T")[0];
  };
  const shiftYMDByDays = (ymd: string, deltaDays: number): string => {
    const d = new Date(ymd + "T12:00:00");
    d.setUTCDate(d.getUTCDate() + deltaDays);
    return d.toISOString().split("T")[0];
  };

  // Detect a single-milestone due-date change (the only case where cascade
  // makes sense). Multi-edits are saved as-is without a prompt.
  const detectCascadeOpportunity = () => {
    const dueDateEdits = Object.entries(pendingMilestoneChanges).filter(([_, c]) => "dueDate" in c);
    if (dueDateEdits.length !== 1) return null;
    const [editedId, edit] = dueDateEdits[0];
    const editedMs = milestones.find((m) => m.id === editedId);
    if (!editedMs?.dueDate) return null;
    const newDueYMD = edit.dueDate as string; // YYYY-MM-DD from input
    const oldDueYMD = toYMD(editedMs.dueDate);
    const newMs = +new Date(newDueYMD + "T12:00:00");
    const oldMs = +new Date(oldDueYMD + "T12:00:00");
    const deltaDays = Math.round((newMs - oldMs) / 86400_000);
    if (deltaDays === 0) return null;
    const editedSort = (editedMs as any).sortOrder ?? 0;
    const downstream = milestones
      .filter((m) => m.id !== editedId)
      .filter((m) => ((m as any).sortOrder ?? 0) > editedSort)
      .filter((m) => ((m as any).progress ?? 0) < 100)
      .map((m) => ({
        id: m.id,
        name: m.name,
        oldStartDate: m.startDate ? toYMD(m.startDate) : null,
        oldDueDate: m.dueDate ? toYMD(m.dueDate) : null,
      }));
    if (downstream.length === 0) return null;
    return { editedName: editedMs.name, deltaDays, downstream };
  };

  const handleSave = async () => {
    const cascade = detectCascadeOpportunity();
    if (cascade) {
      setCascadePrompt(cascade);
      return;
    }
    await doSave();
  };

  const doSave = async (extraMilestoneChanges: Record<string, Record<string, any>> = {}) => {
    setIsSaving(true);
    try {
      if (pendingProjectName !== null || pendingStartDate !== null || pendingCompanyId !== null) {
        const projectUpdate: any = {};
        if (pendingProjectName !== null) projectUpdate.name = pendingProjectName;
        if (pendingStartDate !== null) projectUpdate.startDate = new Date(pendingStartDate + "T12:00:00").toISOString();
        if (pendingCompanyId !== null) projectUpdate.companyId = pendingCompanyId;
        await new Promise<void>((resolve, reject) => {
          updateProject.mutate(projectUpdate, { onSuccess: () => resolve(), onError: (e) => reject(e) });
        });
      }

      // Merge user-edited changes with cascade-shifted extras (extras only
      // apply to milestones that weren't directly edited).
      const merged: Record<string, Record<string, any>> = { ...pendingMilestoneChanges };
      for (const [id, changes] of Object.entries(extraMilestoneChanges)) {
        merged[id] = { ...(merged[id] || {}), ...changes };
      }

      // Save milestone changes — convert YMD strings to ISO, recompute
      // duration from final start/due so DB stays consistent.
      for (const [milestoneId, raw] of Object.entries(merged)) {
        const changes = { ...raw };
        if (changes.dueDate && typeof changes.dueDate === "string" && changes.dueDate.length === 10) {
          changes.dueDate = new Date(changes.dueDate + "T12:00:00").toISOString();
        }
        if (changes.startDate && typeof changes.startDate === "string" && changes.startDate.length === 10) {
          changes.startDate = new Date(changes.startDate + "T12:00:00").toISOString();
        }
        // Recompute duration if either date changed in this update
        if (changes.startDate || changes.dueDate) {
          const ms = milestones.find((m) => m.id === milestoneId);
          const finalStart = changes.startDate ?? ms?.startDate;
          const finalDue = changes.dueDate ?? ms?.dueDate;
          if (finalStart && finalDue) {
            const days = Math.max(
              1,
              Math.round((+new Date(finalDue) - +new Date(finalStart)) / 86400_000) + 1,
            );
            changes.duration = days;
          }
        }
        await new Promise<void>((resolve, reject) => {
          updateMilestone.mutate({ milestoneId, ...changes }, { onSuccess: () => resolve(), onError: (e) => reject(e) });
        });
      }

      setPendingProjectName(null);
      setPendingStartDate(null);
      setPendingCompanyId(null);
      setPendingMilestoneChanges({});
      setCascadePrompt(null);
      toast.success("Changes saved");
    } catch (err: any) {
      toast.error(err?.message || "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setPendingProjectName(null);
    setPendingStartDate(null);
    setPendingCompanyId(null);
    setPendingMilestoneChanges({});
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPendingStartDate(e.target.value);
    setShowRecalculate(true);
  };

  const confirmDateChange = (recalculate: boolean) => {
    if (recalculate && pendingStartDate) {
      // Recalculate is immediate — it changes all milestones server-side
      updateProject.mutate(
        { startDate: new Date(pendingStartDate + "T12:00:00").toISOString(), recalculateDates: true },
        { onSuccess: () => { toast.success("All dates recalculated"); setPendingStartDate(null); } }
      );
    }
    // If not recalculating, just keep it as a pending change
    setShowRecalculate(false);
  };

  const handleAddTask = (milestoneId: string) => {
    const name = newTaskName[milestoneId]?.trim();
    if (!name) return;
    createTask.mutate({ milestoneId, name });
    setNewTaskName((prev) => ({ ...prev, [milestoneId]: "" }));
  };

  const handleAddMilestone = () => {
    const name = newMilestone.name.trim();
    if (!name) return;
    // Duration auto-derives from start + due dates (or defaults to 1).
    let computedDuration = 1;
    if (newMilestone.startDate && newMilestone.dueDate) {
      const s = new Date(newMilestone.startDate + "T12:00:00");
      const d = new Date(newMilestone.dueDate + "T12:00:00");
      computedDuration = Math.max(1, Math.round((+d - +s) / 86400_000) + 1);
    }
    const payload: any = {
      name,
      priority: newMilestone.priority,
      duration: computedDuration,
    };
    if (newMilestone.owner && newMilestone.owner !== "__none__") payload.owner = newMilestone.owner;
    if (newMilestone.startDate) payload.startDate = new Date(newMilestone.startDate + "T12:00:00").toISOString();
    if (newMilestone.dueDate) payload.dueDate = new Date(newMilestone.dueDate + "T12:00:00").toISOString();
    createMilestone.mutate(payload, {
      onSuccess: () => {
        setNewMilestone(emptyNewMilestone);
        setAddMilestoneOpen(false);
      },
      onError: (e: any) => toast.error(e?.message || "Failed to create milestone"),
    });
  };

  const cycleStatus = (task: ProjectTask) => {
    const next = task.status === "todo" ? "in_progress" : task.status === "in_progress" ? "done" : "todo";
    updateTask.mutate({ taskId: task.id, status: next });
  };

  const displayName = pendingProjectName !== null ? pendingProjectName : project.name;
  const displayStartDate = pendingStartDate !== null ? pendingStartDate : (project.startDate ? new Date(project.startDate).toISOString().split("T")[0] : "");

  const overallProgress = milestones.length > 0
    ? Math.round(milestones.reduce((s, m) => s + (getMilestoneVal(m, "progress") || 0), 0) / milestones.length)
    : 0;

  return (
    <div className="space-y-5 max-w-4xl">
      {/* ═══ Project Basics ═══ */}
      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Project Name</Label>
              <Input
                value={displayName}
                onChange={(e) => setPendingProjectName(e.target.value)}
                className="text-lg font-semibold border-0 border-b rounded-none px-0 h-auto py-1 focus-visible:ring-0"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Linked Client</Label>
              <Select
                value={pendingCompanyId !== null ? pendingCompanyId : (project.companyId || "__none__")}
                onValueChange={(v) => setPendingCompanyId(v === "__none__" ? "" : v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="No client linked" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No client linked</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4 items-end mt-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Start Date</Label>
              <Input type="date" value={displayStartDate} onChange={handleDateChange} className="h-9" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">Progress</Label>
              <div className="flex items-center gap-2 h-9">
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div className={cn("h-full rounded-full", overallProgress === 100 ? "bg-green-500" : "bg-primary")}
                    style={{ width: `${overallProgress}%` }} />
                </div>
                <span className="text-sm font-bold">{overallProgress}%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ═══ Client Share Link ═══ */}
      <ShareLinkCard projectId={project.id} />

      {/* ═══ Team & Activity (collapsible) ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center gap-2 p-4">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold text-sm">Team</span>
            <div className="flex items-center gap-1 ml-2">
              {project.teamMemberRecords.map((tm) => (
                <Avatar key={tm.id} className="w-6 h-6">
                  <AvatarFallback className="text-[10px]">{tm.name[0]}</AvatarFallback>
                </Avatar>
              ))}
            </div>
            <span className="text-xs text-muted-foreground ml-1">All team members</span>
          </div>
        </Card>

        <Collapsible open={activityOpen} onOpenChange={setActivityOpen}>
          <Card>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/20 transition-colors">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-muted-foreground" />
                  <span className="font-semibold text-sm">Activity Log</span>
                  <Badge variant="secondary" className="text-[10px]">{activityLogs.length}</Badge>
                </div>
                <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform", activityOpen && "rotate-180")} />
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0 pb-4 px-4 max-h-[250px] overflow-y-auto space-y-2">
                {activityLogs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No activity yet</p>
                ) : activityLogs.slice(0, 20).map((log) => (
                  <div key={log.id} className="flex items-start gap-2 text-xs">
                    <Badge variant={log.type === "success" ? "default" : log.type === "warning" ? "destructive" : "secondary"}
                      className="text-[9px] mt-0.5 shrink-0">{log.type}</Badge>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{log.action}</span>
                      {log.details && <span className="text-muted-foreground"> — {log.details}</span>}
                    </div>
                    <span className="text-muted-foreground whitespace-nowrap shrink-0">
                      {new Date(log.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                ))}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>

      {/* ═══ Milestones Accordion ═══ */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Milestones</h3>
        <Button size="sm" variant="outline" className="gap-1" onClick={() => { setNewMilestone(emptyNewMilestone); setAddMilestoneOpen(true); }}>
          <Plus className="w-3.5 h-3.5" /> Add Milestone
        </Button>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {milestones.map((m, idx) => {
          const prog = getMilestoneVal(m, "progress") || 0;
          const isComplete = prog === 100;
          const tasks = m.tasks || [];
          const doneTasks = tasks.filter((t) => t.status === "done").length;

          return (
            <AccordionItem key={m.id} value={m.id} className="border rounded-lg overflow-hidden">
              <AccordionTrigger className={cn(
                "px-4 py-3 hover:no-underline hover:bg-muted/20 [&[data-state=open]]:bg-muted/10",
                isComplete && "bg-green-500/5"
              )}>
                <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <span className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0",
                    isComplete ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"
                  )}>
                    {isComplete ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <span className={cn("font-medium text-sm", isComplete && "line-through text-muted-foreground")}>{m.name}</span>
                    <div className="flex items-center gap-3 mt-0.5">
                      {(() => { const p = getMilestoneVal(m, "priority"); return (
                        <Badge variant="outline" className={cn("text-[10px] h-4",
                          p === "High" ? "border-red-300 text-red-600 dark:border-red-800 dark:text-red-400" :
                          p === "Medium" ? "border-amber-300 text-amber-600 dark:border-amber-800 dark:text-amber-400" : "border-slate-300 text-slate-500 dark:border-slate-800"
                        )}>{p}</Badge>
                      ); })()}
                      {getMilestoneVal(m, "dueDate") && (
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(getMilestoneVal(m, "dueDate")).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })}
                        </span>
                      )}
                      {getMilestoneVal(m, "owner") && <span className="text-[11px] text-muted-foreground">{getMilestoneVal(m, "owner")}</span>}
                      {tasks.length > 0 && (
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <ListTodo className="w-3 h-3" />
                          {doneTasks}/{tasks.length}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-16 shrink-0 text-right">
                    <span className={cn("text-sm font-bold", isComplete ? "text-green-500" : "")}>{prog}%</span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 pt-2">
                <div className="space-y-4">
                  {/* Progress */}
                  <div className="flex items-center gap-3">
                    <Slider
                      value={[prog]}
                      onValueChange={([v]) => queueMilestoneChange(m.id, "progress", v)}
                      min={0} max={100} step={5} className="flex-1"
                    />
                    {prog < 100 ? (
                      <Button size="sm" className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700 text-white shrink-0"
                        onClick={() => queueMilestoneChange(m.id, "progress", 100)}>
                        <Check className="w-3 h-3" /> Complete
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 shrink-0"
                        onClick={() => queueMilestoneChange(m.id, "progress", 0)}>
                        <X className="w-3 h-3" /> Reopen
                      </Button>
                    )}
                  </div>

                  {/* Details grid */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Start Date</Label>
                      <Input type="date" className="h-8 text-xs"
                        value={(() => { const v = getMilestoneVal(m, "startDate"); return v ? (typeof v === "string" && v.length === 10 ? v : new Date(v).toISOString().split("T")[0]) : ""; })()}
                        onChange={(e) => queueMilestoneChange(m.id, "startDate", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Due Date</Label>
                      <Input type="date" className="h-8 text-xs"
                        value={(() => { const v = getMilestoneVal(m, "dueDate"); return v ? (typeof v === "string" && v.length === 10 ? v : new Date(v).toISOString().split("T")[0]) : ""; })()}
                        onChange={(e) => queueMilestoneChange(m.id, "dueDate", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Duration</Label>
                      <div className="h-8 text-xs flex items-center px-2 rounded-md border border-dashed text-muted-foreground bg-muted/30 tabular-nums" title="Computed from start and due dates">
                        {(() => {
                          const sv = getMilestoneVal(m, "startDate");
                          const dv = getMilestoneVal(m, "dueDate");
                          if (!sv || !dv) return "—";
                          const s = new Date(typeof sv === "string" && sv.length === 10 ? sv + "T12:00:00" : sv);
                          const d = new Date(typeof dv === "string" && dv.length === 10 ? dv + "T12:00:00" : dv);
                          const days = Math.max(1, Math.round((+d - +s) / 86400_000) + 1);
                          return `${days} day${days === 1 ? "" : "s"}`;
                        })()}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Priority</Label>
                      <Select value={getMilestoneVal(m, "priority") || "Medium"} onValueChange={(v) => queueMilestoneChange(m.id, "priority", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] uppercase text-muted-foreground">Owner</Label>
                      <Select value={getMilestoneVal(m, "owner") || "TEAM"} onValueChange={(v) => queueMilestoneChange(m.id, "owner", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {teamMembers.map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Separator />

                  {/* Sub-tasks */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-semibold uppercase text-muted-foreground flex items-center gap-1">
                        <ListTodo className="w-3.5 h-3.5" /> Tasks
                        {tasks.length > 0 && <span className="font-normal">({doneTasks}/{tasks.length} done)</span>}
                      </Label>
                    </div>

                    {tasks.length > 0 && (
                      <div className="space-y-1 mb-3">
                        {tasks.map((task) => (
                          <div key={task.id} className={cn(
                            "flex items-center gap-2 p-2 rounded-md border transition-colors",
                            task.status === "done" ? "bg-green-500/5 border-green-200/50 dark:border-green-900" : "bg-card border-border"
                          )}>
                            <button
                              className={cn(
                                "w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors cursor-pointer",
                                task.status === "done" ? "bg-green-500 border-green-500" :
                                task.status === "in_progress" ? "border-amber-400 bg-amber-50 dark:bg-amber-950/40" : "border-slate-300 dark:border-slate-800"
                              )}
                              onClick={() => cycleStatus(task)}
                              title={`Status: ${statusLabel[task.status || "todo"]} — click to change`}
                            >
                              {task.status === "done" && <Check className="w-3 h-3 text-white" />}
                              {task.status === "in_progress" && <div className="w-2 h-2 rounded-full bg-amber-400" />}
                            </button>

                            <span className={cn("flex-1 text-sm min-w-0 truncate",
                              task.status === "done" && "line-through text-muted-foreground"
                            )}>{task.name}</span>

                            <input
                              type="date"
                              aria-label="Start date"
                              title="Start date"
                              value={(task as any).startDate ? new Date((task as any).startDate).toISOString().split("T")[0] : ""}
                              onChange={(e) => updateTask.mutate({
                                taskId: task.id,
                                startDate: e.target.value ? new Date(e.target.value + "T12:00:00").toISOString() : null,
                              })}
                              className="h-6 text-[10px] border rounded px-1 bg-background w-[110px]"
                            />
                            <span className="text-muted-foreground text-[10px]">→</span>
                            <input
                              type="date"
                              aria-label="Due date"
                              title="Due date"
                              value={task.dueDate ? new Date(task.dueDate).toISOString().split("T")[0] : ""}
                              onChange={(e) => updateTask.mutate({
                                taskId: task.id,
                                dueDate: e.target.value ? new Date(e.target.value + "T12:00:00").toISOString() : null,
                              })}
                              className="h-6 text-[10px] border rounded px-1 bg-background w-[110px]"
                            />
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step={10}
                              aria-label="Percent complete"
                              title="Percent complete"
                              value={(task as any).percentComplete ?? 0}
                              onChange={(e) => {
                                const n = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                updateTask.mutate({ taskId: task.id, percentComplete: n });
                              }}
                              className="h-6 text-[10px] border rounded px-1 bg-background w-[50px] text-right"
                            />
                            <span className="text-muted-foreground text-[10px] -ml-1">%</span>

                            <Select value={task.assigneeId || "_unassigned"} onValueChange={(v) => {
                              const user = crmUsers.find((u) => u.id === v);
                              updateTask.mutate({ taskId: task.id, assigneeId: v === "_unassigned" ? "" : v, assigneeName: user?.name || undefined });
                            }}>
                              <SelectTrigger className="h-6 w-24 text-[10px] border-0 bg-transparent px-1">
                                <SelectValue placeholder="Assign" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_unassigned">Unassigned</SelectItem>
                                {crmUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                              </SelectContent>
                            </Select>

                            <Button variant="ghost" size="icon" className="h-5 w-5 shrink-0 text-muted-foreground hover:text-destructive"
                              onClick={() => deleteTask.mutate(task.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a task..."
                        className="h-8 text-sm flex-1"
                        value={newTaskName[m.id] || ""}
                        onChange={(e) => setNewTaskName((prev) => ({ ...prev, [m.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === "Enter") handleAddTask(m.id); }}
                      />
                      <Button size="sm" className="h-8" variant="outline" disabled={!newTaskName[m.id]?.trim()}
                        onClick={() => handleAddTask(m.id)}>
                        <Plus className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Delete milestone */}
                  <div className="flex justify-end pt-1">
                    <Button variant="ghost" size="sm" className="text-xs text-muted-foreground hover:text-destructive gap-1"
                      onClick={() => setDeleteConfirmMilestoneId(m.id)}>
                      <Trash2 className="w-3 h-3" /> Remove Milestone
                    </Button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Sticky Save Bar */}
      {hasChanges && (
        <div className="sticky bottom-0 z-30 -mx-6 px-6 py-3 bg-card border-t shadow-lg flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            You have unsaved changes
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDiscard}>Discard</Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving} className="gap-1.5">
              {isSaving ? (
                <>Saving...</>
              ) : (
                <><Check className="w-3.5 h-3.5" /> Save Changes</>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Add Milestone Dialog */}
      <Dialog open={addMilestoneOpen} onOpenChange={(open) => { if (!open) { setAddMilestoneOpen(false); setNewMilestone(emptyNewMilestone); } }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="new-milestone-name" className="text-xs font-semibold uppercase text-muted-foreground">Name</Label>
              <Input
                id="new-milestone-name"
                value={newMilestone.name}
                onChange={(e) => setNewMilestone((p) => ({ ...p, name: e.target.value }))}
                placeholder="Milestone name..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddMilestone();
                  if (e.key === "Escape") { setAddMilestoneOpen(false); setNewMilestone(emptyNewMilestone); }
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase text-muted-foreground">Priority</Label>
                <Select value={newMilestone.priority} onValueChange={(v) => setNewMilestone((p) => ({ ...p, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase text-muted-foreground">Owner</Label>
                <Select value={newMilestone.owner} onValueChange={(v) => setNewMilestone((p) => ({ ...p, owner: v }))}>
                  <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {teamMembers.map((tm) => <SelectItem key={tm} value={tm}>{tm}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase text-muted-foreground">Start Date</Label>
                <Input
                  type="date"
                  value={newMilestone.startDate}
                  onChange={(e) => setNewMilestone((p) => ({ ...p, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase text-muted-foreground">Due Date</Label>
                <Input
                  type="date"
                  value={newMilestone.dueDate}
                  onChange={(e) => setNewMilestone((p) => ({ ...p, dueDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Duration:{" "}
              <span className="font-medium tabular-nums">
                {(() => {
                  if (!newMilestone.startDate || !newMilestone.dueDate) return "set both dates";
                  const s = new Date(newMilestone.startDate + "T12:00:00");
                  const d = new Date(newMilestone.dueDate + "T12:00:00");
                  const days = Math.max(1, Math.round((+d - +s) / 86400_000) + 1);
                  return `${days} day${days === 1 ? "" : "s"}`;
                })()}
              </span>
              <span className="ml-2 opacity-60">— computed from dates</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddMilestoneOpen(false); setNewMilestone(emptyNewMilestone); }}>Cancel</Button>
            <Button onClick={handleAddMilestone} disabled={!newMilestone.name.trim() || createMilestone.isPending}>
              {createMilestone.isPending ? "Creating..." : "Create Milestone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Milestone Confirmation */}
      <AlertDialog open={!!deleteConfirmMilestoneId} onOpenChange={(open) => { if (!open) setDeleteConfirmMilestoneId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Milestone?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this milestone and all its tasks. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => {
              if (deleteConfirmMilestoneId) deleteMilestone.mutate(deleteConfirmMilestoneId);
              setDeleteConfirmMilestoneId(null);
            }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recalculate Dialog */}
      <AlertDialog open={showRecalculate} onOpenChange={setShowRecalculate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Recalculate Dates?</AlertDialogTitle>
            <AlertDialogDescription>
              Shift all milestone dates based on the new start date?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => confirmDateChange(false)}>No, just change start</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmDateChange(true)}>Yes, shift all</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cascade-shift Prompt — shown when one milestone's due date moved */}
      <AlertDialog
        open={!!cascadePrompt}
        onOpenChange={(open) => { if (!open) setCascadePrompt(null); }}
      >
        <AlertDialogContent className="sm:max-w-[520px]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Shift the rest of the schedule?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  You moved <strong>{cascadePrompt?.editedName}</strong>{" "}
                  {cascadePrompt && cascadePrompt.deltaDays > 0
                    ? `${cascadePrompt.deltaDays} day${cascadePrompt.deltaDays === 1 ? "" : "s"} later`
                    : `${Math.abs(cascadePrompt?.deltaDays ?? 0)} day${Math.abs(cascadePrompt?.deltaDays ?? 0) === 1 ? "" : "s"} earlier`}
                  .
                </p>
                <p>
                  Shift the next{" "}
                  <strong>{cascadePrompt?.downstream.length}</strong>{" "}
                  incomplete milestone{cascadePrompt?.downstream.length === 1 ? "" : "s"} by the same amount?
                </p>
                {cascadePrompt && cascadePrompt.downstream.length > 0 && (
                  <ul className="text-xs text-muted-foreground space-y-1 max-h-40 overflow-y-auto pl-4 list-disc">
                    {cascadePrompt.downstream.slice(0, 8).map((m) => (
                      <li key={m.id}>{m.name}</li>
                    ))}
                    {cascadePrompt.downstream.length > 8 && (
                      <li className="list-none">+ {cascadePrompt.downstream.length - 8} more</li>
                    )}
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">
                  Completed milestones aren't affected.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                // "Just this one" — save without cascade
                setCascadePrompt(null);
                doSave();
              }}
            >
              Just this one
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!cascadePrompt) return;
                const delta = cascadePrompt.deltaDays;
                const extras: Record<string, Record<string, any>> = {};
                for (const m of cascadePrompt.downstream) {
                  const changes: Record<string, any> = {};
                  if (m.oldStartDate) changes.startDate = shiftYMDByDays(m.oldStartDate, delta);
                  if (m.oldDueDate) changes.dueDate = shiftYMDByDays(m.oldDueDate, delta);
                  if (Object.keys(changes).length > 0) extras[m.id] = changes;
                }
                setCascadePrompt(null);
                doSave(extras);
              }}
            >
              Shift all {cascadePrompt?.downstream.length} downstream
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── ShareLinkCard ────────────────────────────────────────────────────────────

function ShareLinkCard({ projectId }: { projectId: string }) {
  const { data, isLoading } = useShareLink(projectId);
  const createLink = useCreateShareLink(projectId);
  const revokeLink = useRevokeShareLink(projectId);
  const extendLink = useExtendShareLink(projectId);
  const updateSettings = useUpdateShareSettings(projectId);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const settings = data?.settings;

  const link = data?.link ?? null;
  const onCopy = async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.url);
    toast.success("Link copied");
  };

  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold">Client Share Link</h3>
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !link ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Generate a read-only link to send to your client. Shows milestones and progress only.
            </p>
            <Button
              size="sm"
              onClick={() => createLink.mutate(undefined as any, {
                onSuccess: () => toast.success("Link created"),
                onError: (e: any) => toast.error(e?.message ?? "Failed to create link"),
              })}
              disabled={createLink.isPending}
            >
              <Plus className="w-4 h-4 mr-1" /> Generate Link
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={link.url} className="font-mono text-xs" />
              <Button size="sm" variant="outline" onClick={onCopy}>
                <Copy className="w-3.5 h-3.5 mr-1" /> Copy
              </Button>
            </div>
            <div className="text-xs text-muted-foreground">
              Generated {new Date(link.createdAt).toLocaleDateString()}. Expires{" "}
              {new Date(link.expiresAt).toLocaleDateString()}.
              {" "}Viewed {link.viewCount} times
              {link.lastViewedAt ? ` (last ${new Date(link.lastViewedAt).toLocaleString()})` : ""}.
              {" "}From {link.distinctIpCount} distinct location{link.distinctIpCount === 1 ? "" : "s"}.
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => extendLink.mutate(
                  { linkId: link.id, extendDays: 90 },
                  { onSuccess: () => toast.success("Extended 90 days") },
                )}
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Extend 90 days
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirmRevoke(true)}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revoke
              </Button>
            </div>
            <AlertDialog open={confirmRevoke} onOpenChange={setConfirmRevoke}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Revoke this share link?</AlertDialogTitle>
                  <AlertDialogDescription>
                    The link will stop working immediately. Generating a new link issues a different URL.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => {
                      revokeLink.mutate(link.id, {
                        onSuccess: () => toast.success("Link revoked"),
                      });
                      setConfirmRevoke(false);
                    }}
                  >
                    Revoke
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
        {settings && (
          <div className="space-y-2 border-t pt-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Page sections
            </div>
            {(
              [
                { key: "showStats", label: "Stat cards" },
                { key: "showCalendar", label: "Calendar view" },
                { key: "showTimeline", label: "Timeline view" },
                { key: "showTracker", label: "Go-to-Market tracker" },
              ] as { key: keyof ShareSettings; label: string }[]
            ).map((s) => (
              <div key={s.key} className="flex items-center justify-between">
                <Label htmlFor={`share-${s.key}`} className="text-sm font-normal">
                  {s.label}
                </Label>
                <Switch
                  id={`share-${s.key}`}
                  checked={settings[s.key]}
                  disabled={updateSettings.isPending}
                  onCheckedChange={(v) =>
                    updateSettings.mutate(
                      { [s.key]: v },
                      { onError: () => toast.error("Failed to update setting") },
                    )
                  }
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
